<script lang="ts">
    import {onMount} from "svelte";
    import {type AdminSummary, type Attendee, authorized_admins, bunkTypes, type Payments, type Registration,} from "@shared/Types.ts";
    import {email, fetchOrigin, page, serverConfig} from "../data.ts";
    import {toFixed} from "@shared/Utils.ts";
    import AttendeeRow from "../AttendeeRow.svelte";

    let summary: AdminSummary;
    let msg = '';

    let editingDialogOpen = false;
    let reg: Registration;
    let payments: Payments;

    async function load() {
        if (!authorized_admins.includes($email)) {
            page.set('EnterEmail');
        }
        const result = await fetchOrigin(`/admin-summary`);
        summary = await result.json();
        if (summary['error']) {
            page.set('EnterEmail');
        }
        const configResult = await fetchOrigin(`/config`);
        $serverConfig = await configResult.json();
    }

    async function postServerConfig() {
        await fetchOrigin(`/config`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify($serverConfig)
        });
    }

    async function editReg(email: string | undefined) {
        if (editingDialogOpen) return;
        if (!email) {
            msg = 'Missing email';
            return;
        }
        reg = await (await fetchOrigin(`/registration/${email}`)).json();
        payments = await (await fetchOrigin(`/payments/${email}`)).json();
        editingDialogOpen = true;
    }

    function createAttendee(): Attendee {
        return {
            lastName: '',
            firstName: '',
            ageOnApril24: '18 or over',
            registeredForRE: false,
            responsibleAdult: '',
            vegetarian: false,
            vegan: false,
            glutenFree: false,
            dairyFree: false,
        };
    }

    function addAttendee() {
        if (!reg.attendees) {
            reg.attendees = [];
        }
        const lastAttendee: Attendee | undefined = reg.attendees.at(-1);
        if (lastAttendee) {
            reg.attendees = [...reg.attendees, JSON.parse(JSON.stringify(lastAttendee)) as Attendee];
        } else {
            let attendee = createAttendee();
            attendee.lastName = reg.lastName;
            attendee.firstName = reg.firstName;
            reg.attendees = [attendee];
        }
    }

    function removeAttendee(i: number) {
        reg.attendees.splice(i, 1);
        reg.attendees = reg.attendees;
    }

    async function saveReg() {
        const regRes = await fetchOrigin(`/registration/${reg.email}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(reg)
        });
        const payRes = await fetchOrigin(`/payments/${reg.email}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payments)
        });
        if ((await regRes.json()).success && (await payRes.json()).success) {
            editingDialogOpen = false;
            load();
        } else {
            alert('Failed to save registration or payments');
        }
    }

    onMount(load)
</script>


<article>
    <h3>Admin Page</h3>
    <p>Welcome to the Admin Page.</p>

    {#if msg}
        <div class="alert alert-warning">{msg}</div>
    {/if}

    {#if summary}
        <p>{summary.registrations.inProgress}/{summary.registrations.max} attendees under in progress registrations</p>
        <progress value={summary.registrations.inProgress} max={summary.registrations.max}/>
        <p>{summary.registrations.completed}/{summary.registrations.max} of attendees under completed registrations</p>
        <small>Completed registrations include those with waived fees.</small>
        <progress value={summary.registrations.completed} max={summary.registrations.max}/>

        <p>{summary.registrationCount} users have started a registration.</p>
        <div style="overflow: auto; max-height: 500px;">
            <table>
                <thead>
                <tr>
                    <th>email</th>
                    <th>name</th>
                    <th>attendees</th>
                    <th>bunk</th>
                    <th>confirmed</th>
                    <th>waitlist</th>
                    <th>edit</th>
                </tr>
                </thead>
                {#each summary.regs as reg}
                    <tr>
                        <td style="white-space: nowrap;">{reg.email}</td>
                        <td style="white-space: nowrap;">{reg.firstName} {reg.lastName}</td>
                        <td style="white-space: nowrap;">{reg.count}</td>
                        <td style="white-space: nowrap;">{reg.sleepingArrangementChoice}</td>
                        <td style="white-space: nowrap;">{reg.confirmed ? 'CONFIRMED' : 'IN PROGRESS'}</td>
                        <td style="white-space: nowrap;">{reg.waitlist ? 'WAITLIST' : ''}</td>
                        <td>
                            <button on:click={() => editReg(reg.email)}>Edit</button>
                        </td>
                    </tr>
                {/each}
            </table>
        </div>

        <p>The following attendees are owed a refund:</p>
        <ul>
            {#each summary.owedRefund ?? [] as owed}
                <li>{owed}</li>
            {:else}
                <li>No attendees are owed a refund</li>
            {/each}
        </ul>
    {/if}

    <a href="/export/tsv">Click here to download the registrations.</a>
</article>

{#if $serverConfig}
    <article>
        <h3>Server Settings</h3>
        <div class="alert alert-warning">Changing these settings may affect registration availability immediately! Be careful.</div>
        <p>
            <label>
                <input type="checkbox" role="switch" bind:checked={$serverConfig.waitlistOnly} on:change={postServerConfig}>
                Waitlist only
            </label>
            <small>Only allow users to register to the waitlist. No payments are collected, but waitlist confirmation emails are sent.</small>
        </p>
        <p>
            <label>
                <input type="checkbox" role="switch" bind:checked={$serverConfig.noNewRegistrations} on:change={postServerConfig}>
                NO NEW REGISTRATIONS
            </label>
            <small>When this is enabled, no new registrations are allowed. (waitlist is also not allowed) Existing registrations can still be modified.</small>
        </p>
    </article>
{/if}

<article>
    <h3>Authorized Users</h3>
    <p>Only the following authorized users are permitted to see this page:</p>
    <ul>
        {#each authorized_admins as admin}
            <li>{admin}</li>
        {:else}
            <li>No authorized admins</li>
        {/each}
    </ul>
    <p>To add an additional user, please contact Forrest at forrestmilner@gmail.com</p>
</article>


<dialog open={editingDialogOpen}>
    <article>
        {#if reg && payments}
            <header>
                <button aria-label="Close" rel="prev" on:click={() => editingDialogOpen = false}></button>
                <p>
                    <strong>Edit {reg.email} registration</strong>
                </p>
            </header>

            <h3>Payments / Confirmation</h3>
            <label>
                confirmed <input type="checkbox" bind:checked={payments.confirmed}>
            </label>
            <label>
                waitlist <input type="checkbox" bind:checked={payments.waitlist}>
            </label>
            payment records:
            <div style="overflow-x: auto;">
                <table>
                    <thead>
                    <tr>
                        <th>Amount</th>
                        <th>Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    {#each payments?.payments ?? [] as payment}
                        <tr>
                            <td style="text-align: center;">{payment.amount === 0 ? 'free confirmation' : ('$' + toFixed(payment.amount, 2))}</td>
                            <td style="text-align: center;">{new Date(payment.date).toLocaleString()}</td>
                        </tr>
                    {:else}
                        <tr>
                            <td colspan="2" style="text-align: center;">No payments yet.</td>
                        </tr>
                    {/each}
                    </tbody>
                </table>
            </div>
            <button on:click={() => {const amount = prompt('Enter the refund amount (do not include $ sign)'); if(amount && parseFloat(amount)) payments.payments = [...payments.payments, {amount: -parseFloat(amount), date: Date.now(), event_id: ''}]}}>
                Add Refund Record
            </button>
            <button on:click={() => {const amount = prompt('Enter the payment amount (do not include $ sign)'); if(amount && parseFloat(amount)) payments.payments = [...payments.payments, {amount: parseFloat(amount), date: Date.now(), event_id: ''}]}}>
                Add Payment Record
            </button>

            <h3>Registration</h3>
            <h4>Part 1 – Who Are You</h4>
            <label>
                First Name
                <input bind:value={reg.firstName}/>
            </label>
            <label>
                Last Name
                <input bind:value={reg.lastName}/>
            </label>
            <label>
                Phone Number
                <input type='tel' bind:value={reg.phoneNumber}/>
            </label>
            <label>
                Preferred Contact Method
                <select bind:value={reg.preferredContactMethod}>
                    <option value="email">Email</option>
                    <option value="text">Text</option>
                </select>
            </label>

            <h4>Part 2 – Who Is Coming</h4>
            <div style="overflow-x: auto;">
                <table>
                    <thead>
                    <tr>
                        <th></th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Age</th>
                        <th>RE</th>
                        <th>Adult</th>
                        <th>Veg</th>
                        <th>Vegan</th>
                        <th>GF</th>
                        <th>DF</th>
                    </tr>
                    </thead>
                    <tbody>
                    {#each (reg?.attendees ?? []) as attendee, i}
                        <AttendeeRow attendee={attendee} on:remove={() => removeAttendee(i)} on:change={(value) => reg.attendees[i] = value.detail}/>
                    {/each}
                    </tbody>
                </table>
            </div>
            <button on:click={addAttendee}>Add Attendee</button>
            <label>
                Additional Dietary Needs
                <textarea bind:value={reg.additionalDietaryNeeds}></textarea>
            </label>

            <h4>Part 3 – Sleeping Arrangements</h4>
            <label>
                Select Your Sleeping Arrangements (Required)
                <select bind:value={reg.sleepingArrangementChoice} required aria-invalid={!reg.sleepingArrangementChoice}>
                    <option value="" disabled selected>Select an option</option>
                    {#each Object.entries(bunkTypes) as [k, v]}
                        <option value={k}>{v}</option>
                    {/each}
                </select>
            </label>
            <label>
                Cabin sharing preferences
                <textarea bind:value={reg.cabinSharing}></textarea>
            </label>
            <label>
                Comments or special requests for sleeping arrangements
                <textarea bind:value={reg.sleepingArrangementsComments}></textarea>
            </label>

            <h4>Part 4 – Additional Accommodations, Ability to Pay</h4>
            <label>
                Additional comments about your registration:
                <textarea bind:value={reg.registrationComments}></textarea>
            </label>
            <label>
                Ride Sharing
                <select bind:value={reg.rideSharing}>
                    <option value=""></option>
                    <option value="I need a ride">I need a ride</option>
                    <option value="I can offer a ride">I can offer a ride</option>
                </select>
            </label>
            <label>
                Ability to pay:
                <select bind:value={reg.abilityToPay} required>
                    <option value="I can pay my fair share">I can pay my fair share</option>
                    <option value="I can pay more than my fair share">I can pay more than my fair share</option>
                    <option value="I can pay a reduced rate">I can pay a reduced rate</option>
                    <option value="Please waive my registration fees">Please waive my registration fees</option>
                    <option value="Specialized Fees">Specialized Fees</option>
                </select>
            </label>
            <label>
                Waiver reason (if applicable)
                <textarea bind:value={reg.waiverReason}></textarea>
            </label>
            <label>
                Donation Amount:
                <input type="number" bind:value={reg.donationAmount} min="0"/>
            </label>

            <h4>Part 5 – Participant Waiver</h4>
            <label>
                <input type="checkbox" bind:checked={reg.liabilityWaiverAccepted}/>
                I Accept the Waiver
            </label>
            <label>
                Full Name:
                <input bind:value={reg.liabilityWaiverAcceptedBy}/>
            </label>
        {/if}
        <button on:click={saveReg}>Save</button>
    </article>
</dialog>